import { useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Users,
  KeyRound,
  Trash2,
  Pencil,
  ShieldCheck,
  Check,
  AlertTriangle,
  X,
} from "lucide-react";
import { adminUsersApi, type AdminUser } from "@/api/adminUsers";
import { Card, CardLabel } from "@/components/ui";

const inputStyle: React.CSSProperties = {
  background: "var(--wf-paper)",
  border: "1px solid var(--wf-ink-5)",
  borderRadius: 8,
  padding: "10px 12px",
  fontSize: 14,
  color: "var(--wf-ink)",
  fontFamily: "var(--wf-font-body)",
  outline: "none",
  width: "100%",
};

type Modal =
  | { kind: "none" }
  | { kind: "edit"; user: AdminUser }
  | { kind: "password"; user: AdminUser }
  | { kind: "delete"; user: AdminUser };

function fmtDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return "—";
  }
}

function ErrorBanner({ message }: { message: string }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 8,
        fontSize: 12,
        color: "var(--nd-orange-pun)",
        background: "#fff1e6",
        border: "1px solid var(--nd-orange)",
        borderRadius: 8,
        padding: "10px 12px",
        lineHeight: 1.5,
      }}
    >
      <AlertTriangle size={13} style={{ marginTop: 1, flexShrink: 0 }} />
      <span>{message}</span>
    </div>
  );
}

function ModalShell({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(8,10,18,0.55)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "var(--wf-paper)",
          border: "1px solid var(--wf-ink-5)",
          borderRadius: 14,
          padding: 22,
          width: "100%",
          maxWidth: 420,
          boxShadow: "0 18px 48px rgba(8,10,18,0.35)",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <h3 style={{ margin: 0, fontSize: 16, fontFamily: "var(--wf-font-head)", color: "var(--wf-ink)" }}>
            {title}
          </h3>
          <button type="button" onClick={onClose} className="wf-btn wf-btn--ghost wf-btn--sm" aria-label="Fechar">
            <X size={14} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function EditModal({ user, onClose }: { user: AdminUser; onClose: () => void }) {
  const qc = useQueryClient();
  const [name, setName] = useState(user.name);
  const [email, setEmail] = useState(user.email);
  const m = useMutation({
    mutationFn: () => adminUsersApi.update(user.id, { name, email }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["admin", "users"] });
      onClose();
    },
  });
  return (
    <ModalShell title="Editar conta" onClose={onClose}>
      <form
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          m.mutate();
        }}
        style={{ display: "flex", flexDirection: "column", gap: 12 }}
      >
        <label style={{ display: "flex", flexDirection: "column", gap: 5, fontSize: 12, color: "var(--wf-ink-3)" }}>
          Nome
          <input style={inputStyle} value={name} onChange={(e) => setName(e.target.value)} required />
        </label>
        <label style={{ display: "flex", flexDirection: "column", gap: 5, fontSize: 12, color: "var(--wf-ink-3)" }}>
          E-mail (login)
          <input style={inputStyle} type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
        </label>
        {m.isError && <ErrorBanner message={(m.error as Error).message} />}
        <button type="submit" disabled={m.isPending} className="wf-btn wf-btn--primary" style={{ marginTop: 4 }}>
          {m.isPending ? "Salvando…" : "Salvar"}
        </button>
      </form>
    </ModalShell>
  );
}

function PasswordModal({ user, onClose }: { user: AdminUser; onClose: () => void }) {
  const [pw, setPw] = useState("");
  const [done, setDone] = useState(false);
  const m = useMutation({
    mutationFn: () => adminUsersApi.resetPassword(user.id, pw),
    onSuccess: () => setDone(true),
  });
  return (
    <ModalShell title="Trocar senha" onClose={onClose}>
      {done ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#3d7d3d" }}>
            <Check size={15} /> Senha trocada. Compartilhe com a pessoa por um canal seguro.
          </div>
          <div style={{ fontSize: 13, color: "var(--wf-ink-2)", lineHeight: 1.5 }}>
            A pessoa entra com <strong>{user.email}</strong> e a nova senha. As sessões antigas dela foram encerradas.
          </div>
          <button type="button" onClick={onClose} className="wf-btn wf-btn--primary">
            Fechar
          </button>
        </div>
      ) : (
        <form
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            m.mutate();
          }}
          style={{ display: "flex", flexDirection: "column", gap: 12 }}
        >
          <p style={{ margin: 0, fontSize: 12, color: "var(--wf-ink-3)", lineHeight: 1.5 }}>
            Defina uma nova senha para <strong style={{ color: "var(--wf-ink-2)" }}>{user.email}</strong>. Não dá pra
            ver a senha atual (ela é criptografada) — você só pode definir uma nova.
          </p>
          <label style={{ display: "flex", flexDirection: "column", gap: 5, fontSize: 12, color: "var(--wf-ink-3)" }}>
            Nova senha
            <input
              style={inputStyle}
              type="text"
              minLength={8}
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              placeholder="Mínimo 8 caracteres"
              required
            />
          </label>
          {m.isError && <ErrorBanner message={(m.error as Error).message} />}
          <button type="submit" disabled={m.isPending || pw.length < 8} className="wf-btn wf-btn--primary" style={{ marginTop: 4 }}>
            {m.isPending ? "Trocando…" : "Trocar senha"}
          </button>
        </form>
      )}
    </ModalShell>
  );
}

function DeleteModal({ user, onClose }: { user: AdminUser; onClose: () => void }) {
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: () => adminUsersApi.remove(user.id),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["admin", "users"] });
      onClose();
    },
  });
  return (
    <ModalShell title="Remover conta" onClose={onClose}>
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <p style={{ margin: 0, fontSize: 13, color: "var(--wf-ink-2)", lineHeight: 1.55 }}>
          Tem certeza que quer remover <strong>{user.name}</strong> ({user.email})? A conta e o acesso dela são
          apagados. Esta ação não tem volta.
        </p>
        {m.isError && <ErrorBanner message={(m.error as Error).message} />}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button type="button" onClick={onClose} className="wf-btn wf-btn--ghost">
            Cancelar
          </button>
          <button
            type="button"
            onClick={() => m.mutate()}
            disabled={m.isPending}
            className="wf-btn"
            style={{ background: "#c0392b", color: "#fff" }}
          >
            {m.isPending ? "Removendo…" : "Remover"}
          </button>
        </div>
      </div>
    </ModalShell>
  );
}

export default function AdminUserList() {
  const [modal, setModal] = useState<Modal>({ kind: "none" });
  const usersQuery = useQuery({ queryKey: ["admin", "users"], queryFn: () => adminUsersApi.list() });

  return (
    <Card padding={24}>
      <CardLabel icon={<Users size={13} color="var(--nd-orange)" />}>
        Contas da plataforma {usersQuery.data ? `(${usersQuery.data.length})` : ""}
      </CardLabel>

      {usersQuery.isLoading && (
        <span className="wf-hand-note" style={{ display: "block", marginTop: 8 }}>
          carregando contas…
        </span>
      )}
      {usersQuery.isError && <ErrorBanner message={(usersQuery.error as Error).message} />}

      {usersQuery.data && (
        <div style={{ overflowX: "auto", marginTop: 12 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: "left", color: "var(--wf-ink-3)", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                <th style={{ padding: "8px 10px" }}>Nome</th>
                <th style={{ padding: "8px 10px" }}>E-mail</th>
                <th style={{ padding: "8px 10px" }}>Criada</th>
                <th style={{ padding: "8px 10px", textAlign: "right" }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {usersQuery.data.map((u) => (
                <tr key={u.id} style={{ borderTop: "1px solid var(--wf-ink-6, var(--wf-ink-5))" }}>
                  <td style={{ padding: "10px 10px", color: "var(--wf-ink)" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      {u.name}
                      {u.isAdmin && (
                        <span
                          title="Administrador"
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 3,
                            fontSize: 10,
                            color: "var(--nd-orange)",
                            border: "1px solid var(--nd-orange)",
                            borderRadius: 20,
                            padding: "1px 7px",
                          }}
                        >
                          <ShieldCheck size={10} /> admin
                        </span>
                      )}
                    </div>
                  </td>
                  <td style={{ padding: "10px 10px", color: "var(--wf-ink-2)" }}>{u.email}</td>
                  <td style={{ padding: "10px 10px", color: "var(--wf-ink-3)" }}>{fmtDate(u.createdAt)}</td>
                  <td style={{ padding: "10px 10px" }}>
                    <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                      <button
                        type="button"
                        title="Editar nome/e-mail"
                        onClick={() => setModal({ kind: "edit", user: u })}
                        className="wf-btn wf-btn--ghost wf-btn--sm"
                      >
                        <Pencil size={13} />
                      </button>
                      <button
                        type="button"
                        title="Trocar senha"
                        onClick={() => setModal({ kind: "password", user: u })}
                        className="wf-btn wf-btn--ghost wf-btn--sm"
                      >
                        <KeyRound size={13} />
                      </button>
                      <button
                        type="button"
                        title="Remover conta"
                        onClick={() => setModal({ kind: "delete", user: u })}
                        className="wf-btn wf-btn--ghost wf-btn--sm"
                        style={{ color: "#c0392b" }}
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modal.kind === "edit" && <EditModal user={modal.user} onClose={() => setModal({ kind: "none" })} />}
      {modal.kind === "password" && <PasswordModal user={modal.user} onClose={() => setModal({ kind: "none" })} />}
      {modal.kind === "delete" && <DeleteModal user={modal.user} onClose={() => setModal({ kind: "none" })} />}
    </Card>
  );
}
